import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, LogOut } from 'lucide-react';

/**
 * SessionTimeoutWarning
 * ─────────────────────
 * Shows a countdown modal when the session is about to expire.
 *
 * [H-4] Previously decoded the JWT client-side to read the `exp` claim.
 * Now receives `expiresAt` (ms timestamp) from App.js, which derives it
 * from the server's `expires_in` field in the login/refresh response.
 * This decouples the component from the token's internal shape.
 *
 * [PHASE-5 2026-10] A session lasts 24 hours from login and can NOT be
 * extended (the server caps every refresh at login + 24 h), so the old
 * "extend session" button is gone: the modal says the session is ending, and
 * offers to sign out now or to dismiss until the last minute.
 *
 * Props:
 *   expiresAt : number            — absolute ms timestamp when the session expires
 *   onLogout  : () => void        — called when user clicks "خروج"
 *   onExpire  : () => void        — called when the session reaches its end
 *                                   (defaults to onLogout)
 */
const SessionTimeoutWarning = ({ expiresAt, onLogout, onExpire }) => {
  const { t, i18n } = useTranslation();
  const [secondsLeft, setSecondsLeft]   = useState(null);
  const [showModal, setShowModal]       = useState(false);
  const [dismissed, setDismissed]       = useState(false);
  const intervalRef                      = useRef(null);
  const WARNING_SECONDS                  = 5 * 60; // show modal 5 min before expiry

  const tick = useCallback(() => {
    const expiry = expiresAt;
    if (!expiry) return;

    const remaining = Math.floor((expiry - Date.now()) / 1000);

    if (remaining <= 0) {
      // Session expired
      clearInterval(intervalRef.current);
      (onExpire || onLogout)();
      return;
    }

    if (remaining <= WARNING_SECONDS) {
      setSecondsLeft(remaining);
      setShowModal(true);
    } else {
      setShowModal(false);
    }
  }, [expiresAt, onLogout, onExpire]);

  // A new expiry (a new session) re-arms the warning.
  useEffect(() => { setDismissed(false); }, [expiresAt]);

  useEffect(() => {
    tick(); // check immediately
    intervalRef.current = setInterval(tick, 10_000); // check every 10 s
    return () => clearInterval(intervalRef.current);
  }, [tick]);

  const urgency = secondsLeft !== null && secondsLeft <= 60; // last minute

  // Dismissed: stay out of the way until the last minute.
  if (!showModal || secondsLeft === null || (dismissed && !urgency)) return null;

  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[200] bg-black/50 backdrop-blur-sm"
        style={{ animation: 'fadeIn 0.2s ease-out' }}
      />

      {/* Modal */}
      <div
        dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
        className="fixed inset-0 z-[201] flex items-center justify-center p-4"
        style={{ animation: 'fadeIn 0.25s ease-out' }}
      >
        <div
          className="relative bg-white rounded-3xl shadow-2xl max-w-sm w-full p-8 text-center"
          style={{ animation: 'slideUp 0.3s cubic-bezier(0.34,1.56,0.64,1)' }}
        >
          {/* Icon */}
          <div
            className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5 ${
              urgency ? 'bg-red-100' : 'bg-amber-100'
            }`}
          >
            <Clock
              className={`w-10 h-10 ${urgency ? 'text-red-600 animate-pulse' : 'text-amber-600'}`}
            />
          </div>

          {/* Countdown */}
          <div
            className={`text-5xl font-black mb-3 font-mono tabular-nums ${
              urgency ? 'text-red-600' : 'text-amber-600'
            }`}
          >
            {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
          </div>

          <h2 className="text-xl font-bold text-gray-900 mb-2">
            {urgency ? t('stw_expiring_now') : t('stw_warning_title')}
          </h2>
          <p className="text-gray-500 text-sm mb-7">
            {urgency
              ? t('stw_urgent_message')
              : t('stw_session_ending_message')}
          </p>

          {/* Progress bar */}
          <div className="w-full bg-gray-100 rounded-full h-1.5 mb-7 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-1000 ${
                urgency ? 'bg-red-500' : 'bg-amber-500'
              }`}
              style={{ width: `${Math.min(100, (secondsLeft / WARNING_SECONDS) * 100)}%` }}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={onLogout}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-gray-200 text-gray-600 font-semibold text-sm hover:bg-gray-50 transition-all"
            >
              <LogOut className="w-4 h-4" />
              {t('stw_logout')}
            </button>
            <button
              onClick={() => setDismissed(true)}
              className="flex-2 flex-[2] flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-white font-bold text-sm transition-all hover:scale-[1.02] active:scale-[0.98]"
              style={{
                background: urgency
                  ? 'linear-gradient(135deg, #dc2626, #b91c1c)'
                  : 'linear-gradient(135deg, #0F766E, #4f46e5)',
                boxShadow: urgency
                  ? '0 8px 24px rgba(220,38,38,0.35)'
                  : '0 8px 24px rgba(15,118,110,0.35)',
              }}
            >
              {t('stw_dismiss')}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn  { from { opacity:0 } to { opacity:1 } }
        @keyframes slideUp {
          from { transform: translateY(30px) scale(0.95); opacity:0 }
          to   { transform: translateY(0)    scale(1);    opacity:1 }
        }
      `}</style>
    </>
  );
};

export default SessionTimeoutWarning;
